import { RouteRecordRaw } from 'vue-router';
import multiguard from 'vue-router-multiguard';
import Root from '@components/Root.vue';
import ProjectOverview from '@components/ProjectOverview/index.vue';
import { authGuard } from '../auth/authGuard';
import { licenseGuard } from '../license/licenseGuard';
import { FilterList } from '@common/filter/types';
import { validateFilterList } from '@common/filter/validateFilterList';
import { quotationFilterValidator } from '@common/filter/quotationFilterValidator';
const SourceOverview = () => import(/* webpackChunkName: "overviews" */ '@components/SourceOverview/index.vue');
const SourceBase = () => import(/* webpackChunkName: "detail" */ '@components/SourceBase.vue');
const UserSettings = () => import(/* webpackChunkName: "misc" */ '@components/UserSettings.vue');
const QuotationExplorer = () => import(/* webpackChunkName: "overviews" */ '@components/QuotationExplorer/index.vue');
const ReportOverview = () => import(/* webpackChunkName: "overviews" */ '@components/Report/ReportOverview/index.vue');
const ReportDetail = () => import(/* webpackChunkName: "reportdetail" */ '@components/Report/ReportDetail.vue');
const CodeOverview = () => import(/* webpackChunkName: "overviews" */ '@components/CodeOverview/index.vue');
const MemoOverview = () => import(/* webpackChunkName: "overviews" */ '@components/MemoOverview/index.vue');
const MemoDetail = () => import(/* webpackChunkName: "detail" */ '@components/MemoDetail.vue');
const Project = () => import(/* webpackChunkName: "overviews" */ '@components/Project.vue');
const ProjectImport = () => import(/* webpackChunkName: "fileimport" */ '@components/ProjectImport.vue');
const ProjectSettings = () => import(/* webpackChunkName: "misc" */ '@components/ProjectSettings.vue');
const PaperSearch = () => import(/* webpackChunkName: "scholar" */ '@components/Scholar/PaperSearch.vue');
const NotFound = () => import(/* webpackChunkName: "misc" */ '@components/NotFound.vue');
const AcceptProjectInvite = () => import(/* webpackChunkName: "auth" */ '@components/Team/AcceptProjectInvite.vue');
const GlobalSearchPage = () => import(/* webpackChunkName: "misc" */ '@components/GlobalSearch/GlobalSearchPage.vue');

const baseChildren: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    redirect: {
      name: 'projects',
    },
  },
  {
    path: '/projects',
    name: 'projects',
    component: ProjectOverview,
    props: (route) => ({ projectType: route.query.type }),
  },
  {
    path: '/settings',
    name: 'usersettings',
    component: UserSettings,
    props: true,
  },
  {
    path: 'project-import/:id',
    name: 'projectImport',
    component: ProjectImport,
    props: true,
  },
  {
    path: '/projects/:projectId',
    component: Project,
    props: true,
    name: 'project',
    redirect: {
      name: 'sources',
    },
    children: [
      {
        path: 'sources',
        name: 'sources',
        component: SourceOverview,
        props: true,
      },
      {
        path: 'sources/:sourceId/i',
        name: 'sourcedetail',
        component: SourceOverview,
        props: true,
      },
      {
        path: 'sources/:sourceId',
        name: 'source',
        component: SourceBase,
        props: (route) => {
          const params: { sourceId: string; initialQuotationId: string; range?: [number, number] } = {
            ...(route.params as { sourceId: string }),
            initialQuotationId: route.query.quotation as string,
          };

          const range = (route.query.range as string)?.split(',').map((val) => Number(val.trim()));

          // Only thing we cannot validate here is that "to" is within the document size
          if (range && range[0] >= 0 && range[0] < range[1]) {
            params.range = range as [number, number];
          }

          return params;
        },
      },
      {
        path: 'quotations',
        name: 'quotations',
        component: QuotationExplorer,
        props: (route) => {
          let initialFilter: FilterList = undefined;

          if (typeof route.query.filter === 'string') {
            try {
              initialFilter = JSON.parse(route.query.filter);

              validateFilterList(initialFilter, quotationFilterValidator);
            } catch (_e) {
              // ignore incorrect arg
              initialFilter = undefined;
            }
          }

          return {
            ...route.params,
            initialFilter,
          };
        },
      },
      {
        path: 'quotations/:quotationId',
        name: 'quotationsdetail',
        component: QuotationExplorer,
        props: true,
      },
      {
        path: 'views',
        name: 'reports',
        component: ReportOverview,
        props: true,
      },
      {
        path: 'views/:reportId',
        name: 'report',
        component: ReportDetail,
        props: true,
      },
      {
        path: 'views/:reportId/quotations/:quotationId',
        name: 'reportWithQuotation',
        component: ReportDetail,
        props: true,
      },
      {
        path: 'codes',
        name: 'codes',
        component: CodeOverview,
        props: true,
      },
      {
        path: 'codes/:codeId',
        name: 'codedetail',
        component: CodeOverview,
        props: true,
      },
      {
        path: 'memos',
        name: 'memos',
        component: MemoOverview,
        props: true,
      },
      {
        path: 'memos/:memoId/i',
        name: 'memodetail',
        component: MemoOverview,
        props: true,
      },
      {
        path: 'memos/:memoId',
        name: 'memo',
        component: MemoDetail,
        props: true,
      },
      {
        path: 'settings',
        name: 'projectsettings',
        component: ProjectSettings,
        props: true,
      },

      {
        path: 'paper-search/:search?/:tab?',
        name: 'paperSearch',
        component: PaperSearch,
        props: true,
      },
      {
        path: 'global-search',
        name: 'globalsearch',
        component: GlobalSearchPage,
        props: true,
      },
      ...(process.env.NODE_ENV === 'development'
        ? [
            {
              path: 'template',
              name: 'projecttemplate',
              component: () => import(/* webpackChunkName: "dev" */ '@components/ProjectTemplate.vue'),
              props: true,
            },
            {
              path: 'testtemplate',
              name: 'testtemplate',
              component: () => import(/* webpackChunkName: "dev" */ '@components/TestTemplate.vue'),
              props: true,
            },
          ]
        : []),
    ],
  },
];

if (process.env.NODE_ENV === 'development') {
  baseChildren.push({
    path: '/project-template-import',
    name: 'projectTemplateImport',
    component: () => import(/* webpackChunkName: "dev" */ '@components/ProjectTemplateImport.vue'),
    props: true,
  });

  baseChildren.push({
    path: '/local-settings',
    name: 'localSettings',
    component: () => import(/* webpackChunkName: "dev" */ '@components/LocalSettings.vue'),
  });
}

const baseRoutes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: Root,
    props: true,
    beforeEnter: multiguard([authGuard, licenseGuard]),
    children: baseChildren,
  },
  {
    path: '/acceptprojectinvite/:token',
    name: 'acceptprojectinvite',
    component: AcceptProjectInvite,
    props: true,
  },
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: NotFound,
  },
];

export default baseRoutes;
